import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControlLabel, MenuItem, Snackbar, TextField, Typography, useTheme,
} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {useClaimCategories} from "../../hooks/useClaimCategories";
import {useClaims} from "../../hooks/useClaims";
import {useClaim} from "../../hooks/useClaim";
import {useClaimCreate} from "../../hooks/useClaimCreate";
import {useClaimUpdate} from "../../hooks/useClaimUpdate";
import {useClaimDelete} from "../../hooks/useClaimDelete";
import {useGroups} from "../../hooks/useGroups";
import {useUserTypes} from "../../hooks/useUserTypes";
import {useBusinessUnitCodes} from "../../hooks/useBusinessUnitCodes";
import {useSetGroupClaims} from "../../hooks/useSetGroupClaims";
import {useSetUserTypeClaims} from "../../hooks/useSetUserTypeClaims";
import {useSetBusinessUnitClaims} from "../../hooks/useSetBusinessUnitClaims";
import {useSetUserClaims} from "../../hooks/useSetUserClaims";

const DEFAULT_CLAIM_TYPE = "permission";
const sameClaim = (a, b) => a != null && b != null && a.claimType === b.claimType && a.claimValue === b.claimValue;

// diff a baseline vs. staged set into delta entries with a `delete` flag
const diffEntries = (baseline, staged, makeEntry) => {
    const b = new Set(baseline), s = new Set(staged);
    const out = [];
    staged.forEach((v) => { if (!b.has(v)) out.push(makeEntry(v, false)); });
    baseline.forEach((v) => { if (!s.has(v)) out.push(makeEntry(v, true)); });
    return out;
};

const ClaimsPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    const [selected, setSelected] = useState(null);       // {claimType, claimValue} | null
    const [filterCategoryId, setFilterCategoryId] = useState(null);
    const [modalType, setModalType] = useState(null);      // 'create'|'details'|'groups'|'userTypes'|'bus'|'users'|null
    const [draft, setDraft] = useState(null);
    const [valueError, setValueError] = useState("");
    const [snackbar, setSnackbar] = useState({open: false, severity: "success", message: ""});

    useEffect(() => {
        const route = routes.find((r) => r.key === "claims");
        if (!route.visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [categories] = useClaimCategories();
    const [claims, refetchClaims, listLoading] = useClaims();
    const [detail, detailLoading, refetchDetail] = useClaim(selected?.claimType, selected?.claimValue, {
        enabled: selected != null,
    });
    const [groups] = useGroups();
    const [userTypes] = useUserTypes();
    const businessUnitCodes = useBusinessUnitCodes();

    const createMutation = useClaimCreate();
    const updateMutation = useClaimUpdate();
    const deleteMutation = useClaimDelete();
    const setGroupClaims = useSetGroupClaims();
    const setUserTypeClaims = useSetUserTypeClaims();
    const setBusinessUnitClaims = useSetBusinessUnitClaims();
    const setUserClaims = useSetUserClaims();

    const accent = theme.palette.secondary.main;
    const showSnackbar = (severity, message) => setSnackbar({open: true, severity, message});
    const closeSnackbar = () => setSnackbar((prev) => ({...prev, open: false}));

    const getCategoryName = (id) => categories.find((c) => c.id === id)?.displayName ?? "—";
    const selectedListItem = useMemo(() => claims.find((c) => sameClaim(c, selected)) ?? null, [claims, selected]);

    // name maps merge the picker option lists with whatever the detail already knows,
    // so current members still render by name even if the option endpoint isn't up yet.
    const groupNameById = useMemo(() => {
        const m = new Map();
        (detail?.groups ?? []).forEach((g) => m.set(g.id, g.name));
        groups.forEach((g) => m.set(g.id, g.name));
        return m;
    }, [groups, detail]);
    const userTypeNameById = useMemo(() => {
        const m = new Map();
        (detail?.userTypes ?? []).forEach((t) => m.set(t.id, t.name));
        userTypes.forEach((t) => m.set(t.id, t.displayName ?? t.name));
        return m;
    }, [userTypes, detail]);

    const countsByCategory = useMemo(() => {
        const map = {};
        claims.forEach((c) => { const k = c.categoryId ?? "none"; map[k] = (map[k] ?? 0) + 1; });
        return map;
    }, [claims]);
    const filteredClaims = useMemo(
        () => (filterCategoryId === null ? claims : claims.filter((c) => c.categoryId === filterCategoryId)),
        [claims, filterCategoryId]
    );

    const selectClaim = (claim) => setSelected({claimType: claim.claimType, claimValue: claim.claimValue});

    // ------------------------------ modals ------------------------------
    const openModal = (type) => {
        setValueError("");
        const src = detail ?? selectedListItem;
        if (type === "create") {
            setDraft({claimType: DEFAULT_CLAIM_TYPE, claimValue: "", displayName: "", categoryId: categories[0]?.id ?? "", description: ""});
        } else if (type === "details") {
            setDraft({claimType: src.claimType, claimValue: src.claimValue, displayName: src.displayName ?? "", categoryId: src.categoryId ?? "", description: src.description ?? ""});
        } else if (type === "groups") {
            setDraft({ids: (detail?.groups ?? []).map((g) => g.id), search: ""});
        } else if (type === "userTypes") {
            setDraft({ids: (detail?.userTypes ?? []).map((t) => t.id), search: ""});
        } else if (type === "bus") {
            setDraft({codes: (detail?.businessUnits ?? []).map((b) => b.code)});
        } else if (type === "users") {
            setDraft({logins: (detail?.users ?? []).map((u) => u.login), loginInput: ""});
        }
        setModalType(type);
    };
    const closeModal = () => { setModalType(null); setDraft(null); setValueError(""); };

    const claimSaveError = (error) => {
        const status = error?.response?.status;
        if (status === 409) { setValueError("A claim with this value already exists."); return; }
        if (status === 400) { showSnackbar("error", "That category no longer exists. Pick another."); return; }
        if (status === 404) { showSnackbar("error", "This claim no longer exists."); refetchClaims(); closeModal(); return; }
        showSnackbar("error", "Something went wrong. Please try again.");
    };

    const saveClaimForm = () => {
        setValueError("");
        const isCreate = modalType === "create";
        const claimValue = (draft.claimValue ?? "").trim();
        const displayName = (draft.displayName ?? "").trim();
        if (isCreate && !claimValue) { setValueError("Value is required."); return; }
        if (!displayName) { showSnackbar("error", "Display Name is required."); return; }
        if (!draft.categoryId) { showSnackbar("error", "Category is required."); return; }
        const payload = {claimType: draft.claimType, claimValue, displayName, categoryId: draft.categoryId, description: (draft.description ?? "").trim()};
        if (isCreate) {
            createMutation.mutate(payload, {
                onSuccess: (created) => {
                    showSnackbar("success", "Claim created.");
                    refetchClaims();
                    setSelected({claimType: created.claimType, claimValue: created.claimValue});
                    closeModal();
                },
                onError: claimSaveError,
            });
        } else {
            updateMutation.mutate({claimType: draft.claimType, claimValue: draft.claimValue, payload}, {
                onSuccess: () => { showSnackbar("success", "Claim updated."); refetchClaims(); refetchDetail(); closeModal(); },
                onError: claimSaveError,
            });
        }
    };

    const handleDelete = () => {
        const src = detail ?? selectedListItem;
        const label = src.displayName || src.claimValue || "this claim";
        if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
        deleteMutation.mutate({claimType: src.claimType, claimValue: src.claimValue}, {
            onSuccess: () => {
                showSnackbar("success", "Claim deleted.");
                refetchClaims();
                setSelected(null);
                closeModal();
            },
            onError: (error) => {
                const status = error?.response?.status;
                if (status === 409) {
                    showSnackbar("warning", "This claim is still in use. Remove it from its groups/assignments before deleting it.");
                } else if (status === 404) {
                    showSnackbar("error", "This claim no longer exists."); refetchClaims(); setSelected(null); closeModal();
                } else {
                    showSnackbar("error", "Something went wrong. Please try again.");
                }
            },
        });
    };

    const assocError = (error) => {
        if (error?.response?.status === 400) showSnackbar("error", "One or more entries were rejected (unknown member or login). No changes applied.");
        else showSnackbar("error", "Something went wrong. Please try again.");
    };
    const assocSuccess = (msg) => { showSnackbar("success", msg); refetchDetail(); closeModal(); };

    const saveGroups = () => {
        const baseline = (detail?.groups ?? []).map((g) => g.id);
        const entries = diffEntries(baseline, draft.ids, (groupId, del) => ({claimType: selected.claimType, claimValue: selected.claimValue, groupId, delete: del}));
        if (!entries.length) { closeModal(); return; }
        setGroupClaims.mutate(entries, {onSuccess: () => assocSuccess("Groups updated."), onError: assocError});
    };
    const saveUserTypes = () => {
        const baseline = (detail?.userTypes ?? []).map((t) => t.id);
        const entries = diffEntries(baseline, draft.ids, (userTypeId, del) => ({claimType: selected.claimType, claimValue: selected.claimValue, userTypeId, delete: del}));
        if (!entries.length) { closeModal(); return; }
        setUserTypeClaims.mutate(entries, {onSuccess: () => assocSuccess("User types updated."), onError: assocError});
    };
    const saveBUs = () => {
        const baseline = (detail?.businessUnits ?? []).map((b) => b.code);
        const entries = diffEntries(baseline, draft.codes, (businessUnitCode, del) => ({claimType: selected.claimType, claimValue: selected.claimValue, businessUnitCode, delete: del}));
        if (!entries.length) { closeModal(); return; }
        setBusinessUnitClaims.mutate(entries, {onSuccess: () => assocSuccess("Business units updated."), onError: assocError});
    };
    const saveUsers = () => {
        const baseline = (detail?.users ?? []).map((u) => u.login);
        const entries = diffEntries(baseline, draft.logins, (login, del) => ({claimType: selected.claimType, claimValue: selected.claimValue, login, delete: del}));
        if (!entries.length) { closeModal(); return; }
        setUserClaims.mutate(entries, {onSuccess: () => assocSuccess("Users updated."), onError: assocError});
    };

    // ------------------------------ list ------------------------------
    const renderRow = (claim) => {
        const sel = sameClaim(claim, selected);
        return (
            <Box key={`${claim.claimType}/${claim.claimValue}`} onClick={() => selectClaim(claim)}
                 sx={{display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, cursor: "pointer",
                     borderBottom: `1px solid ${theme.palette.divider}`, borderLeft: `3px solid ${sel ? accent : "transparent"}`,
                     backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : "transparent",
                     "&:hover": {backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary}}}>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography noWrap sx={{fontWeight: 500}}>{claim.displayName || claim.claimValue}</Typography>
                    <Typography noWrap sx={{fontFamily: "monospace", fontSize: "0.6875rem", color: "text.secondary"}}>{claim.claimValue}</Typography>
                </Box>
                <Chip label={claim.claimType} size="small" sx={{height: 18, fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", backgroundColor: "rgba(0, 163, 255, 0.12)", color: accent}} />
            </Box>
        );
    };

    const groupHeader = (label) => (
        <Typography key={`h-${label}`} sx={{px: 2, pt: 1.5, pb: 0.5, fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.disabled", backgroundColor: theme.palette.background.secondary}}>{label}</Typography>
    );

    const renderList = () => (
        <Box sx={{borderRight: `1px solid ${theme.palette.divider}`, display: "flex", flexDirection: "column", minHeight: 0, backgroundColor: "white"}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: `1px solid ${theme.palette.divider}`}}>
                <Typography variant="body1" sx={{fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary"}}>Claims ({claims.length})</Typography>
                <Button size="small" onClick={() => openModal("create")} sx={{fontWeight: 700}}>+ New</Button>
            </Box>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`}}>
                <Chip label="All" size="small" onClick={() => setFilterCategoryId(null)} variant={filterCategoryId === null ? "filled" : "outlined"} color={filterCategoryId === null ? "secondary" : "default"} />
                {categories.map((cat) => (
                    <Chip key={cat.id} label={`${cat.displayName} (${countsByCategory[cat.id] ?? 0})`} size="small" onClick={() => setFilterCategoryId(cat.id)} variant={filterCategoryId === cat.id ? "filled" : "outlined"} color={filterCategoryId === cat.id ? "secondary" : "default"} />
                ))}
            </Box>
            <Box sx={{flex: 1, overflowY: "auto", minHeight: 0}}>
                {listLoading && claims.length === 0 ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 4}}><CircularProgress size={24} /></Box>
                ) : filterCategoryId === null ? (
                    <>
                        {categories.map((cat) => {
                            const rows = filteredClaims.filter((c) => c.categoryId === cat.id);
                            return rows.length ? [groupHeader(cat.displayName), ...rows.map(renderRow)] : null;
                        })}
                        {(() => {
                            const uncat = filteredClaims.filter((c) => c.categoryId == null);
                            return uncat.length ? [groupHeader("Uncategorized"), ...uncat.map(renderRow)] : null;
                        })()}
                    </>
                ) : filteredClaims.map(renderRow)}
            </Box>
        </Box>
    );

    // ------------------------------ read-only detail ------------------------------
    const labelSx = {fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

    // Top-level section card (Details, Associations). Edit button only when onEdit is given;
    // pass padded=false to let the body manage its own padding (nested sub-sections).
    const sectionCard = (title, onEdit, body, padded = true) => (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", mb: 2, overflow: "hidden"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.secondary}}>
                <Typography sx={{fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.primary"}}>{title}</Typography>
                {onEdit && <Button size="small" onClick={onEdit} sx={{ml: "auto", fontWeight: 700}}>Edit</Button>}
            </Box>
            <Box sx={{p: padded ? 2 : 0}}>{body}</Box>
        </Box>
    );

    const chipRow = (chips) =>
        chips.length
            ? <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>{chips.map((c) => <Chip key={c.key} label={c.label} size="small" variant="outlined" />)}</Box>
            : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>None</Typography>;

    // One association editor, rendered as a sub-section inside the Associations card.
    const assocSub = (dot, title, source, chips, onEdit, first) => (
        <Box sx={{p: 2, borderTop: first ? "none" : `1px solid ${theme.palette.divider}`}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: dot, flex: "0 0 8px"}} />
                <Typography sx={{fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em"}}>{title}</Typography>
                <Typography component="span" sx={{fontSize: "0.72rem", color: "text.secondary"}}>({chips.length})</Typography>
                <Typography component="span" sx={{fontFamily: "monospace", fontSize: "0.68rem", color: "text.disabled"}}>via {source}</Typography>
                <Button size="small" onClick={onEdit} sx={{ml: "auto", fontWeight: 700}}>Edit</Button>
            </Box>
            {chipRow(chips)}
        </Box>
    );

    const ASSOC_DOT = {group: "#5b9bd5", usertype: "#a78bda", bu: "#2e9e6b", user: "#e08a3c"};

    const renderDetail = () => {
        if (selected == null) {
            return <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", p: 5}}><Typography sx={{fontStyle: "italic"}}>Select a claim to view its details</Typography></Box>;
        }
        if (detailLoading && !detail) {
            return <Box sx={{display: "flex", justifyContent: "center", p: 5}}><CircularProgress size={28} /></Box>;
        }
        const claim = detail ?? selectedListItem;
        if (!claim) return null;
        return (
            <Box sx={{p: 4}}>
                {/* Claim identity */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 3}}>
                    <Box>
                        <Typography variant="h6" sx={{fontWeight: 500}}>{claim.displayName || claim.claimValue}</Typography>
                        <Typography sx={{fontFamily: "monospace", color: "text.secondary", mt: 0.5}}>{claim.claimValue}</Typography>
                    </Box>
                    <Chip label={claim.claimType} size="small" sx={{height: 20, fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", backgroundColor: "rgba(0, 163, 255, 0.12)", color: accent}} />
                </Box>

                {/* SECTION 1: Details */}
                {sectionCard("Details", () => openModal("details"), (
                    <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3}}>
                        <Box><Typography sx={labelSx}>Category</Typography><Typography>{claim.categoryName ?? getCategoryName(claim.categoryId)}</Typography></Box>
                        <Box><Typography sx={labelSx}>Claim Type</Typography><Typography sx={{textTransform: "capitalize"}}>{claim.claimType}</Typography></Box>
                        <Box sx={{gridColumn: "1 / -1"}}><Typography sx={labelSx}>Description</Typography><Typography>{claim.description || <span style={{color: theme.palette.text.disabled, fontStyle: "italic"}}>No description</span>}</Typography></Box>
                    </Box>
                ))}

                {/* SECTION 2: Associations (four editable sub-sections) */}
                {sectionCard("Associations", null, (
                    <Box>
                        {assocSub(ASSOC_DOT.group, "Groups", "group_claim", (detail?.groups ?? []).map((g) => ({key: g.id, label: g.name})), () => openModal("groups"), true)}
                        {assocSub(ASSOC_DOT.usertype, "User Types", "user_type_claim", (detail?.userTypes ?? []).map((t) => ({key: t.id, label: t.name})), () => openModal("userTypes"), false)}
                        {assocSub(ASSOC_DOT.bu, "Business Units", "business_unit_claim", (detail?.businessUnits ?? []).map((b) => ({key: b.code, label: b.code})), () => openModal("bus"), false)}
                        {assocSub(ASSOC_DOT.user, "Users", "user_claim", (detail?.users ?? []).map((u) => ({key: u.login, label: u.name ? `${u.login} · ${u.name}` : u.login})), () => openModal("users"), false)}
                    </Box>
                ), false)}
            </Box>
        );
    };

    // ------------------------------ modal content ------------------------------
    const renderPicker = (options, nameById, onSave, saving) => {
        const q = (draft.search ?? "").trim().toLowerCase();
        const available = options.filter((o) => !draft.ids.includes(o.id) && (!q || (o.name ?? "").toLowerCase().includes(q)));
        return (
            <>
                <DialogContent dividers>
                    <Typography sx={labelSx}>Selected ({draft.ids.length})</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2}}>
                        {draft.ids.length ? draft.ids.map((id) => (
                            <Chip key={id} label={nameById.get(id) ?? id} size="small" onDelete={() => setDraft((d) => ({...d, ids: d.ids.filter((x) => x !== id)}))} />
                        )) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>None selected</Typography>}
                    </Box>
                    <TextField fullWidth size="small" placeholder="Search…" value={draft.search} onChange={(e) => setDraft((d) => ({...d, search: e.target.value}))} sx={{mb: 1}} />
                    <Box sx={{maxHeight: "40vh", overflowY: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px"}}>
                        {available.length ? available.map((o) => (
                            <Box key={o.id} onClick={() => setDraft((d) => ({...d, ids: [...d.ids, o.id]}))}
                                 sx={{display: "flex", justifyContent: "space-between", px: 1.5, py: 1, cursor: "pointer", borderBottom: `1px solid ${theme.palette.divider}`, "&:hover": {backgroundColor: theme.palette.background.secondary}}}>
                                <Typography sx={{fontSize: "0.875rem"}}>{o.name}</Typography>
                                <Typography sx={{fontSize: "0.75rem", color: accent, fontWeight: 600}}>+ Add</Typography>
                            </Box>
                        )) : <Typography sx={{p: 1.5, fontSize: "0.85rem", color: "text.secondary", fontStyle: "italic"}}>{options.length && draft.ids.length === options.length ? "All added" : "No matches"}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button variant="contained" onClick={onSave} disabled={saving}>Save</Button>
                </DialogActions>
            </>
        );
    };

    const renderModal = () => {
        if (!modalType) return null;
        const isForm = modalType === "create" || modalType === "details";
        const title = {create: "New Claim", details: "Edit Details", groups: "Edit Groups", userTypes: "Edit User Types", bus: "Edit Business Units", users: "Edit Users"}[modalType];
        const sub = modalType === "create" ? "" : (selected?.claimValue ?? "");

        return (
            <Dialog open onClose={closeModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {title}
                    {sub && <Typography sx={{fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary"}}>{sub}</Typography>}
                </DialogTitle>

                {isForm && (
                    <>
                        <DialogContent dividers>
                            <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2}}>
                                <TextField label="Value" size="small" required value={draft.claimValue} disabled={modalType === "details"}
                                           error={!!valueError} helperText={valueError || (modalType === "details" ? "Machine key — cannot be changed." : "Machine key — the permission identifier.")}
                                           onChange={(e) => { setValueError(""); setDraft((d) => ({...d, claimValue: e.target.value})); }} />
                                <TextField label="Display Name" size="small" required value={draft.displayName} onChange={(e) => setDraft((d) => ({...d, displayName: e.target.value}))} />
                            </Box>
                            <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2}}>
                                <TextField label="Claim Type" size="small" select value={draft.claimType} disabled={modalType === "details"} onChange={(e) => setDraft((d) => ({...d, claimType: e.target.value}))}>
                                    <MenuItem value="permission">Permission</MenuItem>
                                </TextField>
                                <TextField label="Category" size="small" select required value={draft.categoryId} onChange={(e) => setDraft((d) => ({...d, categoryId: e.target.value}))}>
                                    {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.displayName}</MenuItem>)}
                                </TextField>
                            </Box>
                            <TextField label="Description" size="small" fullWidth multiline rows={3} value={draft.description} onChange={(e) => setDraft((d) => ({...d, description: e.target.value}))} />
                        </DialogContent>
                        <DialogActions sx={{justifyContent: "space-between"}}>
                            <Box>{modalType === "details" && <Button color="error" onClick={handleDelete} disabled={deleteMutation.isPending}>Delete</Button>}</Box>
                            <Box>
                                <Button onClick={closeModal}>Cancel</Button>
                                <Button variant="contained" onClick={saveClaimForm} disabled={createMutation.isPending || updateMutation.isPending}>{modalType === "create" ? "Create" : "Save"}</Button>
                            </Box>
                        </DialogActions>
                    </>
                )}

                {modalType === "groups" && renderPicker(groups, groupNameById, saveGroups, setGroupClaims.isPending)}
                {modalType === "userTypes" && renderPicker(userTypes.map((t) => ({...t, name: t.displayName ?? t.name})), userTypeNameById, saveUserTypes, setUserTypeClaims.isPending)}

                {modalType === "bus" && (
                    <>
                        <DialogContent dividers>
                            {businessUnitCodes.map((bu) => (
                                <FormControlLabel key={bu.id} control={
                                    <Checkbox checked={draft.codes.includes(bu.id)} onChange={(e) => setDraft((d) => ({...d, codes: e.target.checked ? [...d.codes, bu.id] : d.codes.filter((c) => c !== bu.id)}))} />
                                } label={bu.name} />
                            ))}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button variant="contained" onClick={saveBUs} disabled={setBusinessUnitClaims.isPending}>Save</Button>
                        </DialogActions>
                    </>
                )}

                {modalType === "users" && (
                    <>
                        <DialogContent dividers>
                            <Box sx={{display: "flex", gap: 1, mb: 2}}>
                                <TextField size="small" fullWidth placeholder="Enter login…" value={draft.loginInput}
                                           onChange={(e) => setDraft((d) => ({...d, loginInput: e.target.value}))}
                                           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLogin(); } }} />
                                <Button variant="contained" onClick={addLogin}>Add</Button>
                            </Box>
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                                {draft.logins.length ? draft.logins.map((login) => (
                                    <Chip key={login} label={login} size="small" onDelete={() => setDraft((d) => ({...d, logins: d.logins.filter((l) => l !== login)}))} />
                                )) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>No users</Typography>}
                            </Box>
                            <Typography sx={{mt: 2, fontSize: "0.75rem", color: "text.disabled", fontStyle: "italic"}}>Logins aren't checked here — the API validates each login on save.</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button variant="contained" onClick={saveUsers} disabled={setUserClaims.isPending}>Save</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        );
    };

    const addLogin = () => setDraft((d) => {
        const v = (d.loginInput ?? "").trim();
        if (!v || d.logins.includes(v)) return {...d, loginInput: ""};
        return {...d, logins: [...d.logins, v], loginInput: ""};
    });

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Manage Claims" />
            <Box sx={{flex: 1, minHeight: 0, p: 3, display: "flex"}}>
                <Box sx={{flex: 1, display: "grid", gridTemplateColumns: "320px 1fr", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px", overflow: "hidden", minHeight: 0}}>
                    {renderList()}
                    <Box sx={{display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto", backgroundColor: "white"}}>{renderDetail()}</Box>
                </Box>
            </Box>

            {renderModal()}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ClaimsPage;
